<template>
  <Modal
    :show="isOpen"
    elem-id="modal_wizard_add_servico"
    modal-size="modal-sm"
    modal-size-mobile="modal-lg"
    :title="t('Adicionar serviço')"
    :closeLabel="t('Cancelar')"
    :saveLabel="t('Adicionar serviço')"
    :saveFn="handleAddService"
    @close="handleClose"
  >
    <div class="modal-content-body">
      <p class="info-text">
        {{ t('Use o sistema existente para adicionar um novo serviço ao compromisso') }}.
      </p>
      <Message severity="info" :closable="false">
        {{
          t(
            'Após adicionar o serviço, você será redirecionado para a etapa de fotos "Antes" para enviar as fotos do novo serviço'
          )
        }}
      </Message>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '../../../ui/Modal.vue';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

interface ModalOpenData {
  compromissoId?: number;
  orcamentoId?: number;
  franquiaId?: number;
  [key: string]: unknown;
}

interface Props {
  compromissoId?: number | null;
  orcamentoId?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  compromissoId: null,
  orcamentoId: null
});

const emit = defineEmits<{
  'close': [];
  'service-added': [];
}>();

const { t } = useI18n();

// State
const isOpen = ref<boolean>(false);
const currentCompromissoId = ref<number | null>(null);
const currentOrcamentoId = ref<number | null>(null);
const currentFranquiaId = ref<number>(0);

// Event handlers for centralized ModalManager
const handleModalOpen = (data: ModalOpenData): void => {
  log('WizardAddServicoModal', 'handleModalOpen', 'Modal open event received', data);
  if (data?.compromissoId) {
    currentCompromissoId.value = data.compromissoId;
  }
  if (data?.orcamentoId) {
    currentOrcamentoId.value = data.orcamentoId;
  }
  currentFranquiaId.value = Number(data?.franquiaId) || 0;
  open();
};

// Methods
const open = (): void => {
  log('WizardAddServicoModal', 'open', 'Opening add service modal');
  isOpen.value = true;
};

const handleClose = (): void => {
  log('WizardAddServicoModal', 'handleClose', 'Closing modal');
  isOpen.value = false;
  emit('close');
};

// Hand over to the centralised CompromissoServicoModal in NEW mode (no `id` in
// the payload => abreModalNovo). It is the one form that creates a
// compromisso_servico; this modal only explains the hand-over. The wizard reloads
// itself on `compromisso.servico.saved` (plan doutor-flow-test-coverage §3 S3).
const handleAddService = (): void => {
  const compromissoId = currentCompromissoId.value || props.compromissoId;
  const orcamentoId = currentOrcamentoId.value || props.orcamentoId;
  log('WizardAddServicoModal', 'handleAddService', 'Opening CompromissoServicoModal (new) via eventBus', {
    compromissoId,
    orcamentoId
  });
  eventBus.emit('modal.compromisso-servico.open', {
    compromissoId: Number(compromissoId) || 0,
    orcamentoId: Number(orcamentoId) || 0,
    franquiaId: currentFranquiaId.value
  });
  handleClose();
  emit('service-added');
};

// Lifecycle
onMounted(() => {
  log('WizardAddServicoModal', 'mounted', 'Component mounted');
  eventBus.on('modal.wizard-add-servico.open', handleModalOpen as (data?: unknown) => void);
  eventBus.on('modal.wizard-add-servico.close', handleClose);
});

onBeforeUnmount(() => {
  log('WizardAddServicoModal', 'beforeUnmount', 'Cleaning up');
  eventBus.off('modal.wizard-add-servico.open', handleModalOpen as (data?: unknown) => void);
  eventBus.off('modal.wizard-add-servico.close', handleClose);
});

defineExpose({ open });
</script>

<style scoped>
.modal-content-body {
  padding: 10px 0;
}

.info-text {
  font-size: 16px;
  margin-bottom: 20px;
  line-height: 1.6;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .info-text {
    font-size: 14px;
  }
}
</style>
